import React, { useEffect, useRef } from 'react'

import { Tooltip } from '@chakra-ui/react'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { usePutLanguageV2ApplyMutation } from '@core/app/api/applyJobV2Api'
import type { ApplyV2TempLanguage } from '@core/app/types/applyV2Type'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2SelectSingle from '@core/components/ApplyDialogV2/ApplyV2SelectSingle'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import { languagesData } from '@core/utils/helpers/languagesData'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import './ApplyV2LanguageStep2Edit.scss'

interface Props {
    className?: string
    onClose?: () => void
    item: ApplyV2TempLanguage
}

interface FormValues {
    worldLanguageId: number
    languageLevelId: 1 | 2 | 3 | 4 | 5
}

const baseClass = 'ApplyV2LanguageStep2Edit'

function ApplyV2LanguageStep2Edit({ className, onClose = () => {}, item }: Props) {
    const { getTitle } = useApplyV2NavTranslations()
    const { t } = useTranslation()
    const { storageData, postProfileRefetch } = useApplyV2Context()
    const firstRef = useRef<any>(null)
    const [putLanguage] = usePutLanguageV2ApplyMutation()

    useEffect(() => {
        if (firstRef.current) {
            firstRef.current.focus()
        }
    }, [])

    const LANGUAGE_LEVELS = [
        { label: t('10597', 'Elementary'), value: 1 },
        { label: t('10598', 'Limited working'), value: 2 },
        { label: t('10599', 'Professional working'), value: 3 },
        { label: t('10600', 'Full professional'), value: 4 },
        { label: t('10601', 'Native or bilingual'), value: 5 },
    ]

    const validationSchema = Yup.object().shape({
        worldLanguageId: Yup.number().required(getTitle('6730', 'Language is required')),
        languageLevelId: Yup.number()
            .typeError(getTitle('6731', 'Proficiency level is required'))
            .required(getTitle('6731', 'Proficiency level is required'))
            .oneOf([1, 2, 3, 4, 5], getTitle('6731', 'Proficiency level is required')),
    })

    const formik = useFormik<FormValues>({
        initialValues: {
            worldLanguageId: item.worldLanguageId ?? null,
            languageLevelId: item.languageLevelId ?? null,
        },
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting, setErrors }) => {
            try {
                const uid = storageData?.postInitUid
                if (!uid || !values.worldLanguageId || !values.languageLevelId) {
                    return
                }
                const res = await putLanguage({
                    uid,
                    body: {
                        uid: item.uid,
                        languageLevelId: values.languageLevelId,
                    },
                })
                await postProfileRefetch()
            } catch (error: any) {
                if (error?.data?.errors && Array.isArray(error.data.errors)) {
                    const formikErrors: { [key: string]: string } = {}
                    error.data.errors.forEach((err: any) => {
                        switch (err.field) {
                            case 'worldLanguageId':
                            case 'languageLevelId':
                                formikErrors[err.field] = err.msg
                                break
                            default:
                                formikErrors.root = err.msg
                                break
                        }
                    })

                    setErrors(formikErrors)
                } else {
                    setErrors({
                        root: error.message,
                    } as any)
                }
            }
        },
        enableReinitialize: true,
        validateOnChange: true,
    })

    return (
        <form className={clsx(baseClass, className)} onSubmit={formik.handleSubmit} noValidate>
            <div className={`${baseClass}__header`}>
                <div className={`${baseClass}__header__title`}>{getTitle('10933', 'Edit language')}</div>
                <Tooltip
                    hasArrow
                    // isDisabled={isLoading}
                    openDelay={500}
                    label={getTitle('181', 'Close')}
                    // placement={'right'}
                    padding={'8px 16px'}
                    borderRadius={'4px'}
                    backgroundColor={'#19191A'}
                    fontWeight={600}
                    fontSize={'12px'}
                    shouldWrapChildren
                >
                    <ApplyV2Button
                        className={`${baseClass}__header__btn`}
                        size={'small'}
                        variant="transparent"
                        onClick={onClose}
                        leftIcon={
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M13.6239 2.19526C13.8843 2.45561 13.8843 2.87772 13.6239 3.13807L8.8524 7.90963L13.8047 12.8619C14.0651 13.1223 14.0651 13.5444 13.8047 13.8047C13.5444 14.0651 13.1223 14.0651 12.8619 13.8047L7.90959 8.85243L3.13807 13.6239C2.87772 13.8843 2.45561 13.8843 2.19526 13.6239C1.93491 13.3636 1.93491 12.9415 2.19526 12.6811L6.96678 7.90963L2.37605 3.31886C2.1157 3.05851 2.11571 2.6364 2.37606 2.37605C2.63641 2.11571 3.05852 2.11571 3.31887 2.37606L7.90959 6.96682L12.6811 2.19526C12.9415 1.93491 13.3636 1.93491 13.6239 2.19526Z"
                                    fill="#19191A"
                                />
                            </svg>
                        }
                    />
                </Tooltip>
            </div>
            <ApplyV2SelectSingle
                // ref={firstRef}
                name="worldLanguageId"
                label={getTitle('95', 'Language')}
                value={formik.values.worldLanguageId}
                options={Object.values(languagesData).map((l) => ({
                    label: l.name,
                    value: l.languageId,
                }))}
                onChange={(e) => {
                    formik.setFieldValue('worldLanguageId', e)
                }}
                onBlur={formik.handleBlur}
                placeholder={getTitle('10931', 'Select or type a language')}
                errorMessage={formik.touched.worldLanguageId && formik.errors.worldLanguageId}
                isRequired
                isDisabled
            />
            <ApplyV2SelectSingle
                // ref={firstRef}
                name="languageLevelId"
                label={getTitle('10963', 'Proficiency level')}
                value={formik.values.languageLevelId}
                options={LANGUAGE_LEVELS}
                onChange={(e) => {
                    formik.setFieldValue('languageLevelId', e)
                }}
                onBlur={formik.handleBlur}
                placeholder={getTitle('10932', 'Select proficiency level')}
                errorMessage={formik.touched.languageLevelId && formik.errors.languageLevelId}
                isRequired
            />
            <div className={`${baseClass}__btns`}>
                <ApplyV2Button
                    type={'reset'}
                    variant="transparent"
                    className={`${baseClass}__btns--btn ${baseClass}__btns--btn--submit`}
                    // isLoading={formik.isSubmitting}
                    onClick={() => {
                        // console.log(formik.values, formik.touched, formik.errors)
                        formik.resetForm()
                        onClose()
                    }}
                >
                    {getTitle('416', 'Cancel')}
                </ApplyV2Button>
                <ApplyV2Button
                    type={'submit'}
                    // type={'button'}
                    variant="primary"
                    className={`${baseClass}__btns--btn ${baseClass}__btns--btn--submit`}
                    isLoading={formik.isSubmitting}
                    isDisabled={!formik.isValid || formik.isSubmitting || !formik.dirty}
                    // isDisabled={!formik.isValid || !formik.dirty}
                    // onClick={() => {
                    //     console.log('Submit clicked', formik.values)
                    //     console.log('Formik state:', formik.touched)
                    //     console.log('Formik errors:', formik.errors)
                    //     console.log('Formik isValid:', formik.isValid, formik.dirty)
                    // }}
                >
                    {getTitle('12', 'Save')}
                </ApplyV2Button>
            </div>
        </form>
    )
}

export default ApplyV2LanguageStep2Edit
